/**
 * The Velvet Wine Club sections.
 * Handles reveal animations and Shopify Theme Editor lifecycle events.
 */
(() => {
  'use strict';

  if (window.TVCWineClub) {
    window.TVCWineClub.init(document);
    return;
  }

  const instances = new Map();
  const reduceMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

  const getSections = (root) => {
    const sections = [];

    if (root instanceof Element && root.matches('[data-tvc-section]')) {
      sections.push(root);
    }

    if (root.querySelectorAll) {
      sections.push(...root.querySelectorAll('[data-tvc-section]'));
    }

    return [...new Set(sections)];
  };

  const revealAll = (section) => {
    section.querySelectorAll('[data-tvc-reveal]').forEach((item) => {
      item.classList.add('is-visible');
    });
  };

  const initialiseSection = (section) => {
    if (instances.has(section)) return;

    const revealEnabled = section.dataset.tvcRevealEnabled === 'true';
    const items = [...section.querySelectorAll('[data-tvc-reveal]')];

    if (!revealEnabled || reduceMotionQuery.matches || !('IntersectionObserver' in window)) {
      revealAll(section);
      instances.set(section, { observer: null });
      return;
    }

    const animateOnce = section.dataset.tvcAnimateOnce !== 'false';
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');

            if (animateOnce) {
              observer.unobserve(entry.target);
            }
          } else if (!animateOnce) {
            entry.target.classList.remove('is-visible');
          }
        });
      },
      {
        root: null,
        rootMargin: '0px 0px -8% 0px',
        threshold: 0.12,
      }
    );

    items.forEach((item) => observer.observe(item));
    instances.set(section, { observer });
  };

  const destroySection = (section) => {
    const instance = instances.get(section);

    if (!instance) return;

    instance.observer?.disconnect();
    instances.delete(section);
  };

  const init = (root = document) => {
    document.documentElement.classList.add('tvc-js');
    getSections(root).forEach(initialiseSection);
  };

  const destroy = (root) => {
    getSections(root).forEach(destroySection);
  };

  const handleReducedMotionChange = (event) => {
    if (!event.matches) return;

    instances.forEach((instance, section) => {
      instance.observer?.disconnect();
      revealAll(section);
      instances.set(section, { observer: null });
    });
  };

  const handleBlockSelect = (event) => {
    const block = event.target;

    if (!(block instanceof Element)) return;

    const revealItem = block.matches('[data-tvc-reveal]')
      ? block
      : block.closest('[data-tvc-reveal]');

    if (!revealItem) return;

    revealItem.classList.add('is-visible');
    revealItem.scrollIntoView({ behavior: reduceMotionQuery.matches ? 'auto' : 'smooth', block: 'center' });
  };

  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:section:unload', (event) => destroy(event.target));
  document.addEventListener('shopify:block:select', handleBlockSelect);

  if (typeof reduceMotionQuery.addEventListener === 'function') {
    reduceMotionQuery.addEventListener('change', handleReducedMotionChange);
  } else if (typeof reduceMotionQuery.addListener === 'function') {
    reduceMotionQuery.addListener(handleReducedMotionChange);
  }

  window.TVCWineClub = { init, destroy };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(document), { once: true });
  } else {
    init(document);
  }
})();
